import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { LuxuryButton } from "./LuxuryButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", programme: "", message: "" };

const PROGRAMME_OPTIONS = [
  "Ayurenew Foundation — 3 Months",
  "Ayurenew Transformation — 6 Months",
  "Ayurenew Post Retreat — 9 Months",
  "Not sure yet — guide me",
];

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your discovery call request has been received. We will be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong while sending. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-border/50 py-28 lg:py-40"
    >
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-2 lg:px-12">
        <div>
          <Reveal>
            <p
              data-testid="contact-overline"
              className="text-[11px] font-medium uppercase tracking-[0.4em] text-accent"
            >
              Begin Your Journey
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              data-testid="contact-heading"
              className="mt-8 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            >
              Book a <em className="text-accent">discovery call</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-muted-foreground lg:text-lg">
              Discover a personalised path to wellness. Share a few details and our team will reach
              out to arrange your consultation.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-14 space-y-6 border-l border-border/60 pl-6">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                  Programme Leads
                </p>
                <p className="mt-2 font-serif text-xl italic text-foreground/90">
                  Dr Dineshkumar Singh · Vimla Chendriah
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                  Ayurvedic Doctor
                </p>
                <p className="mt-2 font-serif text-xl italic text-foreground/90">Dr Kirti Malviya</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8 lg:pt-16">
            <div>
              <label htmlFor="contact-name" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                Full Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="luxury-input"
              />
            </div>
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-email" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className="luxury-input"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                  Phone <span className="normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+44 ..."
                  className="luxury-input"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-programme" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                Programme of Interest
              </label>
              <select
                id="contact-programme"
                data-testid="contact-programme-select"
                value={form.programme}
                onChange={set("programme")}
                className="luxury-input appearance-none bg-background"
              >
                <option value="">Select a programme</option>
                {PROGRAMME_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-background text-foreground">
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                Your Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us a little about where you are on your journey — and if this is your first Panchakarma."
                className="luxury-input resize-none"
              />
            </div>
            <div>
              <LuxuryButton data-testid="contact-submit-button" type="submit" disabled={sending}>
                {sending ? "Sending…" : "Request My Discovery Call"}
              </LuxuryButton>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
